import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Search, Users, Shield, UserCheck, UserX } from "lucide-react";
import { fetchEmployees, clearError } from "../../RTK/employees/GetEmployees";
import EmployeeCard from "../../components/Employees/EmployeeCard";
import EmployeeStats from "../../components/Employees/EmployeeStats";
import Loader from "../../components/loader/Loader";

const Employees = () => {
  const dispatch = useDispatch();
  const {
    employees,
    summary,
    loading,
    error,
    pagination
  } = useSelector((state) => state.employees);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchEmployees({ 
      page: currentPage, 
      search: searchTerm 
    }));
  }, [dispatch, currentPage, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading && employees.length === 0) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-[#0f172a] p-4 md:p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-xl shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">الموظفين</h1>
                <p className="text-slate-400">إدارة وعرض بيانات الموظفين</p>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="البحث عن موظف..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pr-12 pl-4 py-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </motion.div>

        {/* Stats */}
        {summary && <EmployeeStats summary={summary} />}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 backdrop-blur-sm"
          >
            {error}
          </motion.div>
        )}

        {/* Employees Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
        >
          {employees.map((employee, index) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              index={index}
            />
          ))}
        </motion.div>

        {/* Empty State */}
        {!loading && employees.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <UserX className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">
              لا توجد نتائج
            </h3>
            <p className="text-slate-500">
              {searchTerm ? "لم يتم العثور على موظفين مطابقين للبحث" : "لا توجد موظفين"}
            </p>
          </motion.div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center items-center gap-2 mt-8"
          >
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.has_previous}
              className="px-4 py-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              السابق
            </button>
            
            <div className="flex gap-1">
              {[...Array(pagination.total_pages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={`px-3 py-2 rounded-xl transition-colors ${
                    currentPage === i + 1
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                      : "bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white hover:bg-slate-700/50"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.has_next}
              className="px-4 py-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-white rounded-xl hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              التالي
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Employees;